import { orgWeekDayOffToMomentDay } from '../subscription/catalog/catalogPlanDayOff';

export type PlanDayOffOption = {
  day: number;
  is_forced?: boolean;
};

export type CheckoutDayOffChip = {
  day: number;
  is_forced: boolean;
};

/** Default subscription off days when the plan has no `no_meals_on_days` options (Ionic parity). */
export function buildDefaultCheckoutDayOffArray(week_day_off: number): number[] {
  const moment_day = orgWeekDayOffToMomentDay(week_day_off);
  if (moment_day != null) {
    return [moment_day];
  }
  return [5];
}

/** Optional plan off-day chips only (forced days stay in `dayOffArray` but are not toggled in UI). */
export function buildCheckoutOptionalDayOffChips(temp_options: PlanDayOffOption[]): CheckoutDayOffChip[] {
  const seen = new Set<number>();
  const chips: CheckoutDayOffChip[] = [];

  for (const option of temp_options) {
    if (seen.has(option.day)) {
      continue;
    }
    seen.add(option.day);
    chips.push({ day: option.day, is_forced: false });
  }

  return chips.sort((a, b) => a.day - b.day);
}

/** All picker chips (forced + optional), sorted Sun→Sat. */
export function buildCheckoutDayOffPickerChips(
  temp_options: PlanDayOffOption[],
  forced_day_offs: number[],
): CheckoutDayOffChip[] {
  const forced_set = new Set(forced_day_offs);
  const chips: CheckoutDayOffChip[] = [];

  for (const day of forced_day_offs) {
    chips.push({ day, is_forced: true });
  }
  for (const option of temp_options) {
    if (!forced_set.has(option.day)) {
      chips.push({ day: option.day, is_forced: false });
    }
  }

  return chips.sort((a, b) => a.day - b.day);
}

/**
 * Toggle an optional weekday off. Limit applies to total selected count (forced + optional),
 * matching Ionic `dayOffArray.length == max_day_off`.
 */
export function toggleCheckoutDayOff(
  day: number,
  selected: number[],
  forced_day_offs: number[],
  max_day_off: number,
): { next: number[]; limit_reached: boolean } {
  if (forced_day_offs.includes(day)) {
    return { next: selected, limit_reached: false };
  }

  const idx = selected.indexOf(day);
  if (idx === -1) {
    if (selected.length >= max_day_off) {
      return { next: selected, limit_reached: true };
    }
    return { next: [...selected, day].sort((a, b) => a - b), limit_reached: false };
  }

  return { next: selected.filter(d => d !== day), limit_reached: false };
}

export function formatCheckoutDayOffSummary(day_offs: number[], weekday_labels: string[]): string {
  if (day_offs.length === 0) {
    return '';
  }
  return day_offs
    .slice()
    .sort((a, b) => a - b)
    .map(d => weekday_labels[d] ?? String(d))
    .join(', ');
}
